import React, { useCallback, useRef, useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { newRefundIntentId } from "../../../lib/refundIntentId";

/**
 * Refund ONE duplicate charge, by its own payment id.
 *
 * Why this control exists (Sep 2026, third independent review of PR #185)
 * ----------------------------------------------------------------------
 * Invariant I-7: *no frontend or operator workflow may accidentally
 * refund the canonical winner when the intended target is a duplicate.*
 *
 * An order that was genuinely collected twice carries two `paid` payment
 * rows. One is the order's own payment; the other is a double charge
 * awaiting refund. Before this, the drawer NAMED the duplicate and then
 * offered exactly one Refund button — `RefundOrderModal`, which always
 * targets the payment that settled the order. An operator trying to
 * return the double charge had no control that could do it, and the one
 * control in front of them would have refunded the customer's
 * legitimate payment instead, leaving the double charge in place and the
 * order looking refunded.
 *
 * So the duplicate gets its own control, next to its own line, carrying
 * its own payment id. The server enforces the same separation from the
 * other side (`routes/payments.py::_guard_duplicate_collection_target`):
 * a duplicate is refunded BY AMOUNT — it has no order lines of its own,
 * because the order's lines were sold once — and a refund of the settling
 * payment while an unresolved duplicate exists must name that payment
 * explicitly.
 *
 * Refunding is real money leaving the business, so it asks first, and the
 * confirmation states the amount and which charge it returns.
 */
export default function DuplicateCollectionRefund({
  duplicate, currency, railLabel, formatMoney, centsToDollars, onRefunded,
}) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // One durable identity per refund INTENT, held across every retry of
  // that intent — the same rule `RefundOrderModal` follows, from the
  // same module, for the same reason: a lost response or an impatient
  // second click must not become two refunds.
  const intentIdRef = useRef(null);
  const beginIntent = useCallback(() => {
    try {
      if (!intentIdRef.current) intentIdRef.current = newRefundIntentId();
      return intentIdRef.current;
    } catch (e) {
      toast.error(
        "This browser cannot generate a secure refund reference. " +
        "Please use an up-to-date browser to issue refunds."
      );
      return null;
    }
  }, []);

  const amountCents = Number(duplicate?.amount_cents || 0);
  const amountLabel = formatMoney(centsToDollars(amountCents),
                                  duplicate?.currency || currency);

  const submit = useCallback(async () => {
    const intentId = beginIntent();
    if (!intentId) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/payments/${duplicate.id}/refund`, {
        // By AMOUNT, never by line items: this charge has no order lines
        // of its own. The server refuses an item allocation against a
        // duplicate for exactly this reason.
        amount_cents: amountCents,
        reason: "Duplicate collection — this order was charged twice.",
        idempotency_key: intentId,
      });
      setDone(true);
      setConfirming(false);
      toast.success(`Duplicate charge of ${amountLabel} refunded.`);
      // A completed refund is a definitive answer, so the NEXT refund is
      // a genuinely new intent.
      intentIdRef.current = null;
      onRefunded && onRefunded();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (detail && (detail.message || detail.code)) ||
        "The duplicate charge could not be refunded. Nothing was changed."
      );
    } finally {
      setSubmitting(false);
    }
  }, [amountCents, amountLabel, beginIntent, duplicate, onRefunded]);

  if (done) {
    return (
      <div className="mt-1 text-[11px] text-emerald-800"
           data-testid={`order-details-duplicate-refunded-${duplicate.id}`}>
        Refunded {amountLabel} for this duplicate charge.
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2"
           data-testid={`order-details-duplicate-confirm-${duplicate.id}`}>
        <span className="text-[11px]">
          Refund {amountLabel} back to the {railLabel} charge? The payment that
          paid this order is not affected.
        </span>
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          data-testid={`order-details-duplicate-refund-confirm-${duplicate.id}`}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-red-400 bg-white hover:bg-red-100 disabled:opacity-60"
        >
          {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
          Yes, refund this duplicate
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => setConfirming(false)}
          className="text-[11px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      data-testid={`order-details-duplicate-refund-${duplicate.id}`}
      className="mt-1 inline-flex items-center text-[11px] px-2 py-1 rounded border border-red-400 bg-white hover:bg-red-100"
    >
      Refund this duplicate charge
    </button>
  );
}
